/**
 * Insights' wire shapes and their mapping to the web types (ADR-0009, wave 17).
 *
 * The contracts are `api/src/competition_insights/modules/insights/README.md`
 * (alerts and trends, stage A) and the digest routes of stage B
 * (`GET /insights/digest`, `POST /insights/digest/regenerate`). Every number
 * and sentence a screen shows is the API's: these mappers rename fields and
 * drop what a surface may not show, and never compute a figure.
 */

import { ApiError } from '@/lib/api'
import { ENTITY_TYPES, type DrillDown, type EntityRef, type EntityType } from '@/lib/drilldown'

import type {
  Alert,
  AlertFact,
  AlertState,
  CitedFact,
  DataMeta,
  Digest,
  DigestPeriod,
  DigestQuery,
  DigestReport,
  DigestWriter,
  FactConfidence,
  FactUnit,
  InsightCategory,
  Severity,
  Trend,
  TrendDirection,
  TrendNotAvailable,
} from './types'

// --- alerts: GET /insights/alerts ----------------------------------------------

/** The states the active list asks for: a dismissed alert is the API's to keep, not the list's to show. */
export const ACTIVE_ALERT_STATES: readonly AlertState[] = ['new', 'acknowledged']

/** The API's largest page: the Overview's count and the Alerts page read one list, so it asks for all it may. */
export const ALERTS_LIMIT = 200

/** `GET /insights/alerts` for the active list. Tenant-wide and production-only by construction: it takes no global filter. */
export function activeAlertsPath(): string {
  const query = new URLSearchParams()
  for (const state of ACTIVE_ALERT_STATES) query.append('state', state)
  query.set('limit', String(ALERTS_LIMIT))
  return `/insights/alerts?${query.toString()}`
}

/** `POST /insights/alerts/{id}/acknowledge|dismiss`. */
export function alertActionPath(id: string, action: 'acknowledge' | 'dismiss'): string {
  return `/insights/alerts/${encodeURIComponent(id)}/${action}`
}

/** A `Fact` from the facts contract (`insight_writer/facts.py`), as the API stores it on an alert. */
export interface InsightFactOut {
  key: string
  kind: string
  basis: 'measured' | 'estimate'
  entity: { type: string; id: string; name: string | null }
  value: string
  unit: FactUnit
  currency: string | null
  period: { start: string; end: string }
  comparison: { basis: string; value: string; period?: { start: string; end: string } | null } | null
  direction: TrendDirection | null
  confidence: FactConfidence
}

/** One item of `GET /insights/alerts` (and the body of acknowledge/dismiss). */
export interface InsightAlertOut {
  id: string
  kind: string
  title: string
  severity: Severity
  category: InsightCategory
  entity: { type: string; id: string; label: string | null } | null
  subject: string
  explanation: string
  occurred_at: string
  first_seen_at: string
  last_seen_at: string
  drill_down: { path: string; params?: Record<string, string> }
  state: AlertState
  acknowledged_at: string | null
  dismissed_at: string | null
  website_id: string | null
  facts: InsightFactOut[]
}

export interface InsightAlertsOut {
  items: InsightAlertOut[]
  meta: { limit: number; truncated: boolean }
}

function isWebEntityType(type: string): type is EntityType {
  return (ENTITY_TYPES as readonly string[]).includes(type)
}

/**
 * The alert's entity as a drill-down entity: only the types the web filters by
 * (a competition, a campaign, a channel), and only with a name to show. A
 * website or funnel step is already in the alert's own drill-down params.
 */
function entityFromWire(entity: InsightAlertOut['entity']): EntityRef | null {
  if (!entity || entity.label === null || !isWebEntityType(entity.type)) return null
  return { type: entity.type, id: entity.id, label: entity.label }
}

function drillDownFromWire(wire: { path: string; params?: Record<string, string> | null }): DrillDown {
  const params = wire.params ?? {}
  return Object.keys(params).length > 0 ? { path: wire.path, params } : { path: wire.path }
}

/**
 * A stored fact for a report surface. Alerts carry measured facts only (the API
 * never stores an estimate on one); one that somehow is not measured is left
 * out here rather than shown as if it were (ADR-0009 D9.11.1).
 */
function alertFactFromWire(fact: InsightFactOut): AlertFact | null {
  if (fact.basis !== 'measured') return null
  return {
    key: fact.key,
    kind: fact.kind,
    entityName: fact.entity.name,
    value: fact.value,
    unit: fact.unit,
    currency: fact.currency,
    period: { start: fact.period.start, end: fact.period.end },
    comparison: fact.comparison
      ? { basis: fact.comparison.basis, value: fact.comparison.value, period: fact.comparison.period ?? null }
      : null,
    direction: fact.direction,
    confidence: fact.confidence,
  }
}

export function insightAlertFromWire(wire: InsightAlertOut): Alert {
  return {
    id: wire.id,
    title: wire.title,
    severity: wire.severity,
    category: wire.category,
    entity: entityFromWire(wire.entity),
    subject: wire.subject,
    explanation: wire.explanation,
    occurredAt: wire.occurred_at,
    drillDown: drillDownFromWire(wire.drill_down),
    state: wire.state,
    facts: wire.facts.flatMap((fact) => alertFactFromWire(fact) ?? []),
  }
}

// --- trends: GET /insights/trends ------------------------------------------------

export const TRENDS_PATH = '/insights/trends'

export interface TrendOut {
  id: string
  title: string
  category: InsightCategory
  unit: string
  current: number | null
  previous: number | null
  change: number | null
  direction: TrendDirection | null
  change_label: string | null
  explanation: string
  entity: null
  sparkline: (number | null)[] | null
  drill_down: { path: string; params?: Record<string, string> }
  not_available: TrendNotAvailable | null
}

export interface TrendsOut {
  items: TrendOut[]
  meta: {
    currency: string
    timezone: string
    week: { start: string; end: string }
    previous_week: { start: string; end: string }
    flat_band: number
  }
}

export function trendFromWire(wire: TrendOut): Trend {
  return {
    id: wire.id,
    title: wire.title,
    category: wire.category,
    direction: wire.direction,
    changeLabel: wire.change_label,
    explanation: wire.explanation,
    entity: null,
    sparkline: wire.sparkline ?? [],
    drillDown: drillDownFromWire(wire.drill_down),
    notAvailable: wire.not_available,
  }
}

/** The trends' period: the last complete week against the week before, as the API framed it. */
export function trendsMeta(meta: TrendsOut['meta']): DataMeta {
  return {
    source: 'live',
    lastUpdatedAt: null,
    currency: meta.currency,
    period: {
      from: meta.week.start,
      to: meta.week.end,
      label: 'Last complete week',
      compareLabel: 'the week before',
      previousFrom: meta.previous_week.start,
      previousTo: meta.previous_week.end,
    },
  }
}

// --- digest: GET /insights/digest, POST /insights/digest/regenerate ----------------

/**
 * The digest envelope. Its own keys are snake_case; `digest` and each fact are
 * the writer's rendered output verbatim, in camelCase with absent optional
 * fields left out (`insight_writer/render.py`), so they are the web `Digest`
 * and `CitedFact` already.
 */
export interface DigestReportOut {
  period: DigestPeriod
  period_start: string
  period_end: string
  version: number | null
  writer: DigestWriter | null
  generated_at: string | null
  digest: Digest | null
  facts: CitedFact[]
  /** Every version of the period, oldest first. */
  versions: { version: number; generated_at: string; writer: DigestWriter }[]
  not_available: { digest?: 'no_digest_yet' }
}

export function digestPath(query: DigestQuery): string {
  const search = new URLSearchParams({ period: query.period })
  if (query.version !== undefined) search.set('version', String(query.version))
  return `/insights/digest?${search.toString()}`
}

export function regenerateDigestPath(period: DigestPeriod): string {
  return `/insights/digest/regenerate?${new URLSearchParams({ period }).toString()}`
}

export function digestReportFromWire(wire: DigestReportOut): DigestReport {
  return {
    period: wire.period,
    periodStart: wire.period_start,
    periodEnd: wire.period_end,
    version: wire.version,
    writer: wire.writer,
    generatedAt: wire.generated_at,
    digest: wire.digest,
    facts: wire.facts,
    // The API lists them oldest first; the picker leads with the latest, so order them here, explicitly.
    versions: wire.versions
      .map((item) => ({ version: item.version, generatedAt: item.generated_at, writer: item.writer }))
      .sort((a, b) => b.version - a.version),
    notAvailable: wire.not_available.digest ?? null,
  }
}

/** The `ApiError.code` of a refused regeneration over the daily limit. */
export const RATE_LIMITED_CODE = 'rate_limited'

/**
 * A regeneration refused over the daily limit (429 with code `rate_limited`):
 * the seconds until it may be asked again, as the API counted them, and the
 * limit. Null for any other error, a 429 with another code included.
 */
export function regenerateLimit(error: unknown): { retryAfterSeconds: number | null; limit: number | null } | null {
  if (!(error instanceof ApiError) || error.status !== 429 || error.code !== RATE_LIMITED_CODE) return null
  const details = error.details as { retry_after_seconds?: unknown; limit?: unknown }
  return {
    retryAfterSeconds: typeof details.retry_after_seconds === 'number' ? details.retry_after_seconds : null,
    limit: typeof details.limit === 'number' ? details.limit : null,
  }
}
